import {getOrderStateTag, getOrderStateText} from "../myOrders/Overview";
import {DataTable} from "../../component/datatable/DataTable";
import {formatPrice, germanDateTime, sumItemsDouble} from "../../js/util";
import {SelectOne} from "../../component/form/SelectOne";
import {orderState, orderStateLabels, orderType, yearsOptions} from "../../js/constants";
import {BreadCrumb} from "../../component/Breadcrumb";
import {OrderItems} from "../../component/OrderItems";
import {SelectReact} from "../../component/form/SelectReact";
import {Fragment, useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {ReactComponent as IconBell} from "../../img/icon/bell.svg";
import {ReactComponent as IconMsg} from "../../img/icon/message.svg";
import {get} from "../../js/api";


export const Overview = ({orders, selectedYear, setSelectedYear}) => {

    const [augmentedOrders, setAugmentedOrders] = useState(orders);

    const [filteredData, setFilteredData] = useState(orders);

    const [orderTypeFilter, setOrderTypeFilter] = useState([])

    const defaultStateFilter = [orderState.OPEN, orderState.PAID, orderState.IN_PROGRESS, orderState.IN_PRODUCTION];
    const [stateFilter, setStateFilter] = useState(defaultStateFilter);

    const availableStates = useMemo(() => {
        console.log([...orderStateLabels.entries()]);
        let entries = [...orderStateLabels.entries()].map(v => {
            return {value: v[0], label: v[1]}
        });
        entries.push({value: 'all', label: 'Alle'})
        return entries;
    }, []);

    const location = useLocation();
    const history = useHistory();

    const augmentedIds = useRef(new Map());

    const columns = [
        {
            name: "Bestellnummer",
            cell: (row) => <>
                {row.id}
                {row.lintnerReferenceNumber && <>
                    <div style={{flexBasis: 100, height: 0}}></div>
                    <small style={{marginLeft: 10}}>{row.lintnerReferenceNumber}</small>
                </>}
            </>,
            selector: (row) => `${row.id} ${row.lintnerReferenceNumber}`,
            style: {
                flexWrap: 'wrap'
            },
            maxWidth: '200px',
            sortable: true,
            conditionalCellStyles: [
                {
                    when: row => row.type == orderType.request,
                    style: {
                        backgroundColor: 'rgba(0, 128, 128, 0.2)'
                    },
                },
                {
                    when: row => row.type == orderType.authorization,
                    style: {
                        backgroundColor: 'rgba(248, 148, 6, 0.2)'
                    },
                },
            ],
        },
        {
            name: "Datum",
            selector: (row) => germanDateTime(row.creationDate),
            sortable: true,
            maxWidth: '200px',
            sortFunction: (first, second) => first.creationDate - second.creationDate,
        },
        {
            name: "Username",
            selector: (row) => row.username ?? row.email.replace('@', '\u200B@'),
            maxWidth: '200px',
            sortable: true,
        },
        {
            name: "Firma",
            selector: (row) => row.billingAddress?.company,
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) => getOrderStateTag(row.state),
            maxWidth: '200px',
            sortFunction: (first, second) => getOrderStateText(first.state).localeCompare(getOrderStateText(second.state)),
            sortable: true,
        },
        {
            name: "Summe (netto)",
            selector: (row) => formatPrice(row.sum),
            right: true,
            sortable: true,
            maxWidth: '200px',
            sortFunction: (first, second) => sumItemsDouble(first.items) - sumItemsDouble(second.items),
        },
        {
            name: "Änderung",
            selector: (row) => germanDateTime(row.lastModification),
            sortable: true,
            sortFunction: (first, second) => first.lastModification - second.lastModification,
        },
        {
            name: "",
            id: "badge",
            cell: (row) => <Fragment>
                <div style={{width: "30px"}}>{row.notificationFlag &&
                    <IconMsg style={{fill: 'red'}} title="Es gibt eine neue Nachricht"/>}</div>
            </Fragment>,
            sortable: true,
            style: {minWidth: "unset !important", flexGrow: 0},
            sortFunction: (first, second) => first.notificationFlag - second.notificationFlag,
        }
    ];

    useEffect(() => {
        var params = new URLSearchParams(location.search);
        const typeFilter = params.get("orderType");
        if (typeFilter) {
            setOrderTypeFilter(typeFilter.split(","));
        } else {
            setOrderTypeFilter([])
        }

        const stateFilter = params.get("state");
        if (stateFilter) {
            setStateFilter(stateFilter?.split(","))
        } else {
            setStateFilter(stateFilter === '' ? [] : defaultStateFilter)
        }
    }, [location.search]);

    const updateStateFilter = (stateFilter) => {
        let newStateFilter = stateFilter;
        if (stateFilter.indexOf('all') > -1) {
            newStateFilter = [...orderStateLabels.keys()];
        }

        addSearch("state", newStateFilter.join(","));
        setStateFilter(newStateFilter);
    }

    const toggleFilter = (orderType) => {
        if (orderTypeFilter.includes(orderType)) {
            orderTypeFilter.splice(orderTypeFilter.indexOf(orderType), 1)
        } else {
            orderTypeFilter.push(orderType);
        }
        setOrderTypeFilter([...orderTypeFilter]);
        addSearch("orderType", orderTypeFilter.join(","));
    }

    useEffect(() => {
        setAugmentedOrders(orders);
        augmentedIds.current.clear();
    }, [orders]);

    useEffect(() => {
        augmentedOrders && setFilteredData(augmentedOrders
            .filter(o => orderTypeFilter.length === 0 || orderTypeFilter.includes(o.type))
            .filter(o => stateFilter.includes(o.state) || o.notificationFlag));
    }, [augmentedOrders, orderTypeFilter, stateFilter])

    const getSearch = (name) => {
        var params = new URLSearchParams(location.search);
        return params.get('expandedEntries');
    }

    const addSearch = (name, value) => {
        const params = new URLSearchParams(location.search);
        params.set(name, value);
        history.push({pathname: location.pathname, search: params.toString()});
    }

    const rowExpanded = (expanded, row) => {
        var selected = getSearch('expandedEntries')?.split(",") || [];
        if (expanded) {
            loadDetail(row);
            if (selected.indexOf(row.id + "") === -1) {
                selected.push(row.id + "");
            }
        } else {
            selected.splice(selected.indexOf(row.id + ""), 1);
        }
        addSearch("expandedEntries", selected.join(","))
    }

    const isRowExpanded = (row) => {
        var selected = getSearch('expandedEntries')?.split(",") || [];
        const expanded = selected.indexOf(row.id + "") !== -1
        if(expanded){
            loadDetail(row)
        }
        return expanded;
    }

    const loadDetail = (row) => {
        if(!augmentedIds.current.has(row.id)){
            augmentedIds.current.set(row.id, false)
            get(`/orders/${row.id}`).then(detailed => {
                setAugmentedOrders(current => {
                    if(!current || !current.some(o => o.id === row.id)){
                        return current;
                    }
                    augmentedIds.current.set(row.id, true)
                    return current.map(o => o.id === row.id ? detailed : o);
                });
            });
        }
    }


    return (
        <div>
            <BreadCrumb currentTitle="Bestellungen"/>
            <div className="default">
                <div>
                    <p className="mr-1"
                       onClick={() => toggleFilter(orderType.standard)}
                       style={{
                           display: "inline",
                           padding: '0.3em',
                           cursor: 'pointer',
                           border: orderTypeFilter.includes(orderType.standard) ? '2px solid var(--highlight)' : '2px solid rgba(0,0,0,0)'
                       }}>Bestellungen</p>
                    <p className="m-1"
                       onClick={() => toggleFilter(orderType.request)}
                       style={{
                           display: "inline",
                           backgroundColor: "rgba(0, 128, 128, 0.2",
                           padding: '0.3em',
                           cursor: 'pointer',
                           border: orderTypeFilter.includes(orderType.request) ? '2px solid var(--highlight)' : '2px solid rgba(0,0,0,0)'
                       }}>Anfragen</p>
                    <p className="m-1"
                       onClick={() => toggleFilter(orderType.authorization)}
                       style={{
                           display: "inline",
                           backgroundColor: "rgba(248, 148, 6, 0.2)",
                           padding: '0.3em',
                           cursor: 'pointer',
                           border: orderTypeFilter.includes(orderType.authorization) ? '2px solid var(--highlight)' : '2px solid rgba(0,0,0,0)'
                       }}>Autorisierungen</p>
                </div>
                <div>
                    <SelectReact name="status" label="" value={stateFilter} menuPortalTarget={document.body}
                                 options={availableStates}
                                 setter={updateStateFilter} isMulti/>
                </div>
            </div>
            <DataTable data={filteredData}
                       columns={columns}
                       expandableRows
                       expandOnRowClicked
                       expandableRowsComponent={OrderItems}
                       expandableRowsComponentProps={{'inline': true}}
                       paginationPerPage={25}
                       actions={<SelectReact value={selectedYear} setter={setSelectedYear}
                                             options={yearsOptions}/>}
                       onRowExpandToggled={rowExpanded}
                       expandableRowExpanded={isRowExpanded}
            />
        </div>
    );

};
